import { useState, useEffect, useCallback, useRef } from 'react';
import { Globe, Copy, RefreshCw, CheckCircle2, Trash2, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';

export const DomainConnect = ({ siteData, onUpdate }) => {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [copied, setCopied] = useState(null);
  const pollRef = useRef(null);

  const loadRecord = useCallback(async () => {
    try {
      const res = await api.get(`/domains/site/${siteData.id}`);
      setRecord(res.data?.domain ? res.data : null);
    } catch {
      setRecord(null);
    } finally {
      setLoading(false);
    }
  }, [siteData.id]);

  useEffect(() => { loadRecord(); }, [loadRecord]);

  const runCheck = useCallback(async (silent = false) => {
    if (!silent) setChecking(true);
    try {
      const res = await api.post(`/domains/site/${siteData.id}/check`);
      setRecord(res.data);
      if (res.data.status === 'live' && !silent) toast.success('Domain connected and live!');
      if (res.data.status === 'live') clearInterval(pollRef.current);
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.detail || 'Check failed');
    } finally {
      if (!silent) setChecking(false);
    }
  }, [siteData.id]);

  // Auto-poll every 45s while awaiting nameserver switch
  useEffect(() => {
    if (record?.status === 'pending') {
      pollRef.current = setInterval(() => runCheck(true), 45000);
      return () => clearInterval(pollRef.current);
    }
  }, [record?.status, runCheck]);

  const handleConnect = async () => {
    if (!input.trim()) return;
    setConnecting(true);
    try {
      const res = await api.post('/domains/connect', { site_id: siteData.id, domain: input.trim() });
      setRecord(res.data);
      onUpdate({ custom_domain: res.data.domain });
      toast.success('Domain added — now change your nameservers');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to connect domain');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(`Disconnect ${record.domain}? The site will only be reachable on its Boxit URL.`)) return;
    try {
      await api.delete(`/domains/site/${siteData.id}`);
      setRecord(null);
      setInput('');
      onUpdate({ custom_domain: null });
      toast.success('Domain disconnected');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disconnect');
    }
  };

  const copyNs = (ns, idx) => {
    navigator.clipboard.writeText(ns);
    setCopied(idx);
    setTimeout(() => setCopied(null), 2000);
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-slate-400 text-sm py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading domain status...</div>;
  }

  // No domain connected yet
  if (!record) {
    return (
      <div className="space-y-4" data-testid="domain-connect-empty">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleConnect()}
            placeholder="yourdomain.com"
            data-testid="domain-connect-input"
            className="flex-1 px-4 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
          />
          <Button
            onClick={handleConnect}
            disabled={connecting || !input.trim()}
            data-testid="domain-connect-btn"
            className="bg-indigo-500 hover:bg-indigo-600 text-white px-5"
          >
            {connecting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <>Connect <ArrowRight className="w-4 h-4 ml-1" /></>}
          </Button>
        </div>
        <p className="text-xs text-slate-400">
          Enter your domain and we'll handle everything — you'll just change your nameservers at your registrar. SSL is automatic.
        </p>
      </div>
    );
  }

  const isLive = record.status === 'live';

  return (
    <div className="space-y-4" data-testid="domain-connect-status">
      {/* Status header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <a
            href={`https://${record.domain}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-white font-semibold hover:text-indigo-300"
            data-testid="connected-domain-link"
          >
            {record.domain}
          </a>
          {isLive ? (
            <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1 rounded-full">
              <CheckCircle2 className="w-3.5 h-3.5" /> Live
            </span>
          ) : (
            <span className="flex items-center gap-1.5 text-xs font-medium text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2.5 py-1 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" /> Awaiting nameservers
            </span>
          )}
        </div>
        <div className="flex gap-2">
          {!isLive && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => runCheck(false)}
              disabled={checking}
              data-testid="domain-check-btn"
              className="text-slate-300 hover:text-white h-8"
            >
              <RefreshCw className={cn('w-3.5 h-3.5 mr-1.5', checking && 'animate-spin')} />
              Check now
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDisconnect}
            data-testid="domain-disconnect-btn"
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Nameserver instructions */}
      {!isLive && (
        <div className="bg-slate-900/50 border border-slate-700/50 rounded-xl p-4 space-y-3">
          <p className="text-sm text-white font-medium">One step left — change your nameservers</p>
          <p className="text-xs text-slate-400">
            Log in to your domain registrar (GoDaddy, Namecheap, 123-reg...), find <span className="text-slate-300">Nameservers</span>, and replace them with these two:
          </p>
          <div className="space-y-2">
            {(record.nameservers || []).map((ns, idx) => (
              <div key={ns} className="flex items-center justify-between bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5">
                <code className="text-indigo-300 font-mono text-sm">{ns}</code>
                <button
                  onClick={() => copyNs(ns, idx)}
                  className="text-xs text-slate-400 hover:text-white flex items-center gap-1"
                  data-testid={`copy-ns-${idx}`}
                >
                  <Copy className="w-3 h-3" /> {copied === idx ? 'Copied!' : 'Copy'}
                </button>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500">
            We check automatically every minute. Nameserver changes usually take 10 minutes to a few hours. Once detected, SSL and routing are set up for you — nothing else to do.
          </p>
        </div>
      )}

      {isLive && (
        <p className="text-xs text-slate-400">
          Your domain is fully connected with automatic SSL. Both <span className="text-slate-300">{record.domain}</span> and <span className="text-slate-300">www.{record.domain}</span> point to this site.
        </p>
      )}
    </div>
  );
};
